import { useState, useEffect, useMemo } from 'react';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, subDays, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import { tr, enUS, nl, fr } from 'date-fns/locale';
import {
  Loader2,
  Calendar as CalendarIcon,
  BarChart3,
  PieChart as PieChartIcon,
  Briefcase,
  Heart,
  Coffee,
  Palmtree,
  Baby,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  CalendarDays,
  Filter
} from 'lucide-react';

const getDateLocale = (lang) => {
  switch (lang) {
    case 'tr': return tr;
    case 'nl': return nl;
    case 'fr': return fr;
    default: return enUS;
  }
};

const STATUS_ICONS = {
  WORKING: Briefcase,
  HALF_DAY: Coffee,
  SICK: Heart,
  DOP: Coffee,
  ON_LEAVE: Palmtree,
  PATERNITY: Baby,
  FEESTDAG: CalendarDays,
  ZATERDAG: CalendarDays,
  NONE: HelpCircle
};

const MONTH_NAMES = {
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  nl: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December']
};

export const MyWorkStatistics = () => {
  const { t, language } = useTranslation();
  const { user } = useAuthStore();
  const dateLocale = getDateLocale(language);
  
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [statistics, setStatistics] = useState(null);
  const [labels, setLabels] = useState({});
  const [colors, setColors] = useState({});
  
  // Date filter states
  const [quickRange, setQuickRange] = useState('thisMonth'); // today, yesterday, lastWeek, thisMonth, custom, year
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [customDateOpen, setCustomDateOpen] = useState(false);
  
  // Local state for date inputs (to prevent fetching on every keystroke)
  const [tempStartDate, setTempStartDate] = useState(dateRange.start);
  const [tempEndDate, setTempEndDate] = useState(dateRange.end);

  // Handle quick range selection
  const handleQuickRange = (range) => {
    setQuickRange(range);
    const now = new Date();
    
    switch (range) {
      case 'today':
        setDateRange({
          start: format(now, 'yyyy-MM-dd'),
          end: format(now, 'yyyy-MM-dd')
        });
        break;
      case 'yesterday':
        const yesterday = subDays(now, 1);
        setDateRange({
          start: format(yesterday, 'yyyy-MM-dd'),
          end: format(yesterday, 'yyyy-MM-dd')
        });
        break;
      case 'lastWeek':
        const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        setDateRange({
          start: format(lastWeekStart, 'yyyy-MM-dd'),
          end: format(lastWeekEnd, 'yyyy-MM-dd')
        });
        break;
      case 'lastMonth':
        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        setDateRange({
          start: format(startOfMonth(lastMonthDate), 'yyyy-MM-dd'),
          end: format(endOfMonth(lastMonthDate), 'yyyy-MM-dd')
        });
        break;
      case 'thisMonth':
        setDateRange({
          start: format(startOfMonth(now), 'yyyy-MM-dd'),
          end: format(endOfMonth(now), 'yyyy-MM-dd')
        });
        break;
      case 'year':
      default:
        setDateRange({
          start: format(new Date(selectedYear, 0, 1), 'yyyy-MM-dd'),
          end: format(new Date(selectedYear, 11, 31), 'yyyy-MM-dd')
        });
        break;
    }
  };

  // Update date range when year changes
  useEffect(() => {
    if (quickRange === 'year') {
      setDateRange({
        start: format(new Date(selectedYear, 0, 1), 'yyyy-MM-dd'),
        end: format(new Date(selectedYear, 11, 31), 'yyyy-MM-dd')
      });
    }
  }, [selectedYear, quickRange]);

  // Sync temp dates when dateRange changes (from quick range buttons)
  useEffect(() => {
    setTempStartDate(dateRange.start);
    setTempEndDate(dateRange.end);
  }, [dateRange.start, dateRange.end]);

  // Fetch data when calendarMonth changes (for calendar view navigation)
  useEffect(() => {
    // Also fetch data for the calendar month range
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    // Only update if we're viewing a different month than what's in dateRange
    const currentRangeStart = new Date(dateRange.start);
    if (monthStart.getMonth() !== currentRangeStart.getMonth() || 
        monthStart.getFullYear() !== currentRangeStart.getFullYear()) {
      // Don't update dateRange, just trigger a fetch for calendar data
      fetchStatistics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth]);

  useEffect(() => {
    fetchStatistics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  // Fetch calendar month data when calendarMonth changes
  const [calendarData, setCalendarData] = useState(null);
  
  useEffect(() => {
    const fetchCalendarData = async () => {
      const monthStart = format(startOfMonth(calendarMonth), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(calendarMonth), 'yyyy-MM-dd');
      try {
        const response = await api.get(`/daily-status/my-statistics?start_date=${monthStart}&end_date=${monthEnd}`);
        if (response.data.success) {
          setCalendarData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching calendar data:', error);
      }
    };
    fetchCalendarData();
  }, [calendarMonth]);

  // Update labels when language changes
  useEffect(() => {
    if (statistics?.labels) {
      setLabels(statistics.labels[language] || statistics.labels.tr);
    }
  }, [language, statistics]);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/daily-status/my-statistics?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      if (response.data.success) {
        setStatistics(response.data.data);
        setLabels(response.data.data.labels[language] || response.data.data.labels.tr);
        setColors(response.data.data.colors);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Get only statuses that have data (count > 0)
  // Get only statuses that have data (count > 0), sorted by count descending
  const activeStatuses = useMemo(() => {
    if (!statistics?.status_counts) return [];
    return Object.entries(statistics.status_counts)
      .filter(([status, count]) => status !== 'NONE' && count > 0)
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .map(([status]) => status);
  }, [statistics]);

  // Prepare data for bar chart - only include active statuses
  const getBarChartData = () => {
    if (!statistics?.monthly_breakdown) return [];
    
    const monthNames = MONTH_NAMES[language] || MONTH_NAMES.tr;
    
    return Object.entries(statistics.monthly_breakdown).map(([monthKey, stats]) => {
      const monthIndex = parseInt(monthKey.split('-')[1]) - 1;
      const data = {
        name: monthNames[monthIndex]?.substring(0, 3) || monthKey,
        fullName: monthNames[monthIndex] || monthKey,
      };
      
      // Only add statuses that the user has
      activeStatuses.forEach(status => {
        data[status] = stats[status] || 0;
      });
      
      return data;
    });
  };

  // Prepare data for pie chart - only include active statuses with count > 0
  const getPieChartData = () => {
    if (!statistics?.status_counts) return [];
    
    return Object.entries(statistics.status_counts)
      .filter(([status, count]) => status !== 'NONE' && count > 0)
      .map(([status, count]) => ({
        name: labels[status] || status,
        value: count,
        color: colors[status] || '#ccc'
      }));
  };

  // Calendar renderer
  const renderCalendar = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
    const startDayOfWeek = getDay(monthStart);
    // Adjust for Monday start (European style)
    const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    
    const weekDays = language === 'tr' 
      ? ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']
      : language === 'nl'
      ? ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    return (
      <div className="space-y-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newDate = new Date(calendarMonth);
              newDate.setMonth(newDate.getMonth() - 1);
              setCalendarMonth(newDate);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-medium text-slate-700 dark:text-slate-200">
            {format(calendarMonth, 'MMMM yyyy', { locale: dateLocale })}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newDate = new Date(calendarMonth);
              newDate.setMonth(newDate.getMonth() + 1);
              setCalendarMonth(newDate);
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Week days header */}
        <div className="grid grid-cols-7 gap-1 text-center">
          {weekDays.map((day, i) => (
            <div key={i} className="text-xs font-medium text-slate-500 dark:text-slate-400 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before month start */}
          {Array.from({ length: adjustedStartDay }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}
          
          {/* Days */}
          {days.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const status = calendarData?.calendar_data?.[dateStr] || statistics?.calendar_data?.[dateStr] || 'NONE';
            const bgColor = status !== 'NONE' ? colors[status] : 'transparent';
            const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;
            
            return (
              <div
                key={dateStr}
                className={cn(
                  "aspect-square flex items-center justify-center text-sm rounded-lg transition-all relative",
                  status !== 'NONE' && "text-white font-medium",
                  isToday && "ring-2 ring-blue-500 ring-offset-1"
                )}
                style={{ 
                  backgroundColor: bgColor,
                  opacity: status === 'NONE' ? 0.4 : 1
                }}
                title={`${format(day, 'd MMMM', { locale: dateLocale })} - ${labels[status] || status}`}
              >
                {format(day, 'd')}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const statusCounts = statistics?.status_counts || {};
  const barData = getBarChartData();
  const pieData = getPieChartData();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-work-statistics">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('workStats.myStatistics') || 'Çalışma İstatistiklerim'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            {t('workStats.myStatisticsDesc') }
          </p>
        </div>
        
        {/* Date Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-wrap items-end gap-4 lg:gap-6">
              {/* Quick Range Buttons */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('workStats.quickRange') || 'Hızlı Seçim'}</Label>
                <div className="flex flex-wrap gap-1">
                  <Button
                    variant={quickRange === 'today' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleQuickRange('today')}
                  >
                    {t('workStats.today') || 'Bugün'}
                  </Button>
                  <Button
                    variant={quickRange === 'yesterday' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleQuickRange('yesterday')}
                  >
                    {t('workStats.yesterday') || 'Dün'}
                  </Button>
                  <Button
                    variant={quickRange === 'lastWeek' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleQuickRange('lastWeek')}
                  >
                    {t('workStats.lastWeek') || 'Geçen Hafta'}
                  </Button>
                  <Button
                    variant={quickRange === 'lastMonth' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleQuickRange('lastMonth')}
                  >
                    {t('workStats.lastMonth') || 'Geçen Ay'}
                  </Button>
                  <Button
                    variant={quickRange === 'thisMonth' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleQuickRange('thisMonth')}
                  >
                    {t('workStats.thisMonth') || 'Bu Ay'}
                  </Button>
                  <Button
                    variant={quickRange === 'year' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleQuickRange('year')}
                  >
                    {t('workStats.thisYear') || 'Bu Yıl'}
                  </Button>
                </div>
              </div>

              {/* Custom Date Range */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('workStats.dateRange') || 'Tarih Aralığı'}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={tempStartDate}
                    onChange={(e) => {
                      setTempStartDate(e.target.value);
                    }}
                    onBlur={(e) => {
                      if (e.target.value && e.target.value !== dateRange.start) {
                        setQuickRange('custom');
                        setDateRange(prev => ({ ...prev, start: e.target.value }));
                      }
                    }}
                    className="w-[145px]"
                  />
                  <span className="text-slate-400 font-medium">-</span>
                  <Input
                    type="date"
                    value={tempEndDate}
                    onChange={(e) => {
                      setTempEndDate(e.target.value);
                    }}
                    onBlur={(e) => {
                      if (e.target.value && e.target.value !== dateRange.end) {
                        setQuickRange('custom');
                        setDateRange(prev => ({ ...prev, end: e.target.value }));
                      }
                    }}
                    className="w-[145px]"
                  />
                </div>
              </div>

              {/* Year Selector */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('workStats.year') || 'Yıl'}</Label>
                <Select value={String(selectedYear)} onValueChange={(v) => {
                  const year = Number(v);
                  setSelectedYear(year);
                  // Always update date range to selected year
                  setQuickRange('year');
                  setDateRange({
                    start: format(new Date(year, 0, 1), 'yyyy-MM-dd'),
                    end: format(new Date(year, 11, 31), 'yyyy-MM-dd')
                  });
                }}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Selected range info */}
            <div className="mt-4 pt-3 border-t flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <Filter className="h-4 w-4 text-blue-500" />
              <span className="font-medium">
                {format(new Date(dateRange.start), 'd MMMM yyyy', { locale: dateLocale })} - {format(new Date(dateRange.end), 'd MMMM yyyy', { locale: dateLocale })}
              </span>
              <span className="text-slate-400 mx-2">|</span>
              <span className="text-slate-600 dark:text-slate-300">
                <strong className="text-blue-600">{statistics?.total_records || 0}</strong> {t('workStats.daysRecorded') || 'gün kaydedildi'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards - Only show statuses that user has */}
      {activeStatuses.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {activeStatuses.map((status) => {
            const count = statusCounts[status] || 0;
            const Icon = STATUS_ICONS[status] || HelpCircle;
            const color = colors[status] || '#ccc';
            return (
              <Card key={status} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <Icon className="h-5 w-5" style={{ color }} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold" style={{ color }}>{count}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{labels[status] || status}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              {t('workStats.monthlyBreakdown') || 'Aylık Dağılım'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {barData.length > 0 && activeStatuses.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value, name) => [value, labels[name] || name]}
                      labelFormatter={(label) => barData.find(d => d.name === label)?.fullName || label}
                    />
                    <Legend formatter={(value) => labels[value] || value} />
                    {activeStatuses.map((status) => (
                      <Bar key={status} dataKey={status} stackId="a" fill={colors[status]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  {t('common.noData') || 'Veri bulunamadı'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-purple-500" />
              {t('workStats.statusDistribution') || 'Durum Dağılımı'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  {t('common.noData') || 'Veri bulunamadı'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar View */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-emerald-500" />
            {t('workStats.calendarView') || 'Takvim Görünümü'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md mx-auto">
            {renderCalendar()}
          </div>
          
          {/* Legend - Only show active statuses */}
          {activeStatuses.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mt-6 pt-4 border-t">
              {activeStatuses.map((status) => (
                <div key={status} className="flex items-center gap-2 text-sm">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: colors[status] }}
                  />
                  <span className="text-slate-600 dark:text-slate-300">{labels[status] || status}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyWorkStatistics;
